import { isValidLocalhost } from "./util/PortUtils";
import { useProfileAuthStore } from "./store/ProfileStore";
import { postJsonWithRetry } from "./util/NetRetry";
const LOGPREFIX = "API: ";
export const getAPIEndpoint = () => {
    const betaURL = "https://8k34jp5jnk.execute-api.us-west-2.amazonaws.com/prod/";
    const prodURL = "https://bl7lx6mjr1.execute-api.us-west-2.amazonaws.com/prod/";
    if (isValidLocalhost()) {
        return betaURL;
    }
    else if (window.location.href.includes("test.mediashare.link")) {
        return betaURL;
    }
    else {
        return prodURL;
    }
};
// Server contract reminder: every bearer encrypts its own sequence number, and
// the API Gateway lambdas additionally enforce a per-(userId, sequence) nonce
// counter that tolerates the same key being seen at most twice before
// rejecting with "Invalid sequence number in request" (see
// infra/lambdas/bearer.ts pushNonce/checkNonce). Concurrent authenticated
// POSTs that share the same bearer will therefore replay the nonce and the
// third such collision throws. To honor that contract we (1) serialize
// authenticated POSTs through a single FIFO queue so only one is in flight at
// a time, and (2) late-bind the Authorization header AND the body's
// `sequence` field from the auth store at the moment of send, so that two
// requests queued back-to-back use successive sequence values rather than the
// stale snapshot captured at call time.
let authedQueueTail = Promise.resolve();
const enqueueAuthenticated = (task) => {
    // Chain off the current tail so tasks run in FIFO order. Swallow errors
    // inside the queue chain so one failing task cannot poison the lock for
    // every subsequent caller — individual callers still observe the failure
    // via their own callback.
    const next = authedQueueTail.then(() => task().catch(() => undefined));
    authedQueueTail = next;
    return next;
};
const sendXhr = (_callback, path, payload, bearer, cacheBusterStore, logger, onComplete) => {
    let completed = false;
    const release = () => {
        if (completed)
            return;
        completed = true;
        if (onComplete)
            onComplete();
    };
    const headers = {
        "Content-type": "application/json",
    };
    if (bearer) {
        headers["Authorization"] = "Basic " + bearer;
    }
    const url = getAPIEndpoint() + path;
    // Retrying while holding the authenticated queue slot (release fires only
    // in onDone) is required for correctness, not just politeness: a status-0
    // failure never reached the server, so the bearer sequence was not
    // consumed, and no other request may go out on this bearer until the
    // retry resolves.
    try {
        postJsonWithRetry({
            url,
            body: JSON.stringify(payload),
            headers,
            onRetryScheduled: (attemptNumber, delayMs) => {
                logger.warn(LOGPREFIX +
                    "sendAjaxRequest: " +
                    path +
                    " network failure (status 0) on attempt " +
                    attemptNumber +
                    ", retrying in " +
                    delayMs +
                    "ms");
            },
            onDone: (status, responseText) => {
                logger.log(LOGPREFIX + "sendAjaxRequest: HTTP status: " + status);
                try {
                    if (status >= 200 && status < 300) {
                        logger.log(LOGPREFIX +
                            "sendAjaxRequest: " +
                            path +
                            " responseText: " +
                            responseText);
                        _callback(true, status, responseText, cacheBusterStore, logger);
                    }
                    else {
                        logger.error(LOGPREFIX +
                            "sendAjaxRequest: " +
                            path +
                            " responseText: " +
                            responseText);
                        _callback(false, status, responseText, cacheBusterStore, logger);
                    }
                }
                finally {
                    // Release the queue regardless of whether the user callback threw,
                    // and regardless of whether the user callback synchronously enqueued
                    // additional requests — those will run after this returns.
                    release();
                }
            },
        });
    }
    catch (err) {
        logger.error(LOGPREFIX + "sendAjaxRequest: " + path + " " + err);
        try {
            _callback(false, 500, err.message, cacheBusterStore, logger);
        }
        finally {
            release();
        }
    }
};
export const sendAjaxRequest = (_callback, path, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
payload, bearer, cacheBusterStore, logger) => {
    logger.log(LOGPREFIX +
        "sendAjaxRequest: path = " +
        path +
        ", payload = " +
        JSON.stringify(payload) +
        ", bearer = " +
        bearer);
    // Unauthenticated POSTs (e.g., contact form, signup) do not consume a
    // bearer sequence and do not need to wait on the authenticated queue.
    if (!bearer) {
        sendXhr(_callback, path, payload, undefined, cacheBusterStore, logger);
        return;
    }
    enqueueAuthenticated(() => new Promise((resolve) => {
        var _a;
        // Late-bind: read the freshest bearer + sequence from the store at
        // the instant the queue grants this task its slot. The store is the
        // single source of truth — the `bearer` parameter passed in by the
        // caller is only used as a hint to decide whether to queue.
        const auth = useProfileAuthStore.getState();
        const latestBearer = (_a = auth.bearer) !== null && _a !== void 0 ? _a : bearer;
        // Rewrite the body's `sequence` AND `bearer` (when present) to match
        // the credentials we are actually sending in the Authorization
        // header. Two reasons both matter:
        //   1. Without rewriting `sequence`, two callers that snapshotted
        //      `auth.sequence` before the previous response landed would
        //      both send the same stale value and the second would be
        //      rejected as a sequence mismatch.
        //   2. Some lambdas (notably chatbot, savePost) read the bearer
        //      from the body — not the Authorization header — and then
        //      check that `body.bearer`'s embedded seq equals `body.sequence`.
        //      Rewriting only sequence without rewriting the body bearer
        //      causes the server to compare a fresh sequence against the
        //      stale bearer's embedded seq, producing exactly the
        //      "Invalid bearer token - invalid sequence number" rejection
        //      we are trying to eliminate.
        if (payload && typeof payload === "object") {
            if ("sequence" in payload && typeof auth.sequence === "number") {
                payload.sequence = auth.sequence;
            }
            if ("bearer" in payload && latestBearer) {
                payload.bearer = latestBearer;
            }
        }
        sendXhr(_callback, path, payload, latestBearer, cacheBusterStore, logger, resolve);
    }));
};
export const sendGETRequest = (_callback, path, cacheBusterStore, logger) => {
    logger.log(LOGPREFIX + "sendGETRequest: path = " + path);
    const xmlhttp = new XMLHttpRequest();
    xmlhttp.onreadystatechange = function () {
        logger.log(LOGPREFIX + "sendGETRequest: " + path + " " + JSON.stringify(xmlhttp));
        if (xmlhttp.readyState == XMLHttpRequest.DONE) {
            // XMLHttpRequest.DONE == 4
            logger.log(LOGPREFIX +
                "sendGETRequest: ReadyState = DONE - HTTP status: " +
                xmlhttp.status +
                ", path = " +
                path);
            const status = +xmlhttp.status;
            if (status >= 200 && status < 300) {
                logger.log(LOGPREFIX +
                    "sendGETRequest: Status OK - " +
                    path +
                    " responseText = " +
                    xmlhttp.responseText);
                _callback(true, status, xmlhttp.responseText, cacheBusterStore, logger);
            }
            else {
                logger.error(LOGPREFIX +
                    "sendGETRequest: Status NOT OK = " +
                    status +
                    " path " +
                    path +
                    " responseText = " +
                    xmlhttp.responseText);
                let text = xmlhttp.responseText;
                if (!text) {
                    text = "Unknown error (" + status + ") for path " + path;
                }
                _callback(false, status, text, cacheBusterStore, logger);
            }
        }
    };
    const url = path;
    xmlhttp.open("GET", url, true);
    xmlhttp.setRequestHeader("Accept", "application/json");
    xmlhttp.send();
};
export const invalidateCloudFront = (channelOwnerId, channelId, postId, sequence, bearer, callback, cacheBusterStore, logger) => {
    logger.log(LOGPREFIX + "invalidateCloudFront: ENTRY");
    const payload = {
        operation: "invalidateCloudFront",
        channelOwnerId,
        channelId,
        postId,
        sequence,
    };
    sendAjaxRequest(callback, "invalidateCloudFront", payload, bearer, cacheBusterStore, logger);
};
